import { useState } from 'react';
import { Link }      from 'react-router-dom';
import { useQuery }  from '@tanstack/react-query';
import {
  MdHistory, MdSearch, MdFilterAlt, MdArrowBack, MdClear, MdSmartToy,
} from 'react-icons/md';
import Card from '../../components/common/Card';
import { getActivityLog } from '../../services/activityService';
import { activityLabel, isSystemActivity } from '../../utils/activityLabels';
import { useCalendar } from '../../context/CalendarContext';
import { adHint } from '../../utils/calendar';
import useTheme from '../../context/ThemeContext';
import useClubVocab from '../../hooks/useClubVocab';

// ── Action styling map ─────────────────────────────────────────────────────────
// Adaptive tints — readable in light & dark, no solid fills or hardcoded grays.
const ACTION_STYLE = {
  payment_recorded:           'bg-emerald-500/15 text-emerald-400',
  payment_confirmed:          'bg-emerald-500/15 text-emerald-400',
  payment_waived:             'bg-nav-hover text-muted',
  fee_waived:                 'bg-nav-hover text-muted',
  fee_restored:               'bg-teal-500/15 text-teal-400',
  proof_approved:             'bg-emerald-500/15 text-emerald-400',
  proof_rejected:             'bg-red-500/15 text-red-400',
  reminder_sent:              'bg-sky-500/15 text-sky-400',
  bulk_reminder_sent:         'bg-sky-500/15 text-sky-400',
  auto_reminder_run:          'bg-sky-500/15 text-sky-400',
  auto_reminder_sent_30:      'bg-sky-500/15 text-sky-400',
  auto_reminder_sent_60:      'bg-sky-500/15 text-sky-400',
  payment_link_generated:     'bg-indigo-500/15 text-indigo-400',
  create_expense:             'bg-amber-500/15 text-amber-400',
  update_expense:             'bg-amber-500/15 text-amber-400',
  approve_expense:            'bg-emerald-500/15 text-emerald-400',
  reject_expense:             'bg-red-500/15 text-red-400',
  archive_expense:            'bg-nav-hover text-muted',
  cancel_expense:             'bg-nav-hover text-muted',
  remove_attachment:          'bg-nav-hover text-muted',
  member_approved:            'bg-emerald-500/15 text-emerald-400',
  member_rejected:            'bg-red-500/15 text-red-400',
  member_application_deleted: 'bg-nav-hover text-muted',
  student_archived:           'bg-orange-500/15 text-orange-400',
  student_deleted:            'bg-red-500/15 text-red-400',
  student_restored:           'bg-teal-500/15 text-teal-400',
  trial_converted:            'bg-teal-500/15 text-teal-400',
  '2fa_enabled':              'bg-emerald-500/15 text-emerald-400',
  '2fa_disabled':             'bg-amber-500/15 text-amber-400',
  admin_login_otp_failed:     'bg-red-500/15 text-red-400',
};

const DEFAULT_ACTION_STYLE = 'bg-nav-hover text-muted';

// adHint (muted AD-month hint) now lives in utils/calendar (KTM-year based).

// Shared filter-bar field style — uniform h-10 height across search / select / dates.
const FIELD_CLS = 'w-full h-10 px-3 text-sm bg-card border border-divider rounded-full text-secondary focus:outline-none focus:border-secondary';

const CATEGORY_LABELS = {
  '': 'All Actions',
  payments:  'Payments',
  expenses:  'Expenses',
  students:  'Students',
};

const CATEGORY_ACTIONS = {
  payments: ['payment_recorded','payment_confirmed','payment_waived','proof_approved','proof_rejected','reminder_sent','payment_link_generated'],
  expenses: ['create_expense','update_expense','approve_expense','reject_expense','archive_expense','cancel_expense','remove_attachment'],
  students: ['student_archived','student_deleted','student_restored','trial_converted'],
};

// ── Relative time helper ───────────────────────────────────────────────────────
function relativeTime(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)  return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function absoluteTime(ts) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ── Activity row ───────────────────────────────────────────────────────────────
function ActivityRow({ log }) {
  const { formatMonth } = useCalendar();
  const system = isSystemActivity(log);
  const style  = ACTION_STYLE[log.action] || DEFAULT_ACTION_STYLE;
  const label  = activityLabel(log);
  const role   = log.metadata?.actorRole || log.actorType;
  const md     = log.metadata || {};

  // Fee months ("2026-06") → BS label + AD hint; other metadata rendered inline.
  const monthText = /^\d{4}-\d{2}$/.test(md.month || '')
    ? `${formatMonth(md.month)} · ${adHint(md.month)}`
    : (md.month || '');
  // Single detail line: student/target · BS month + AD hint · amount · method · receipt#.
  const detailParts = [
    log.targetName && { key: 'target', node: log.targetName },
    monthText && { key: 'month', node: monthText },
    md.amount != null && { key: 'amount', node: `NPR ${Number(md.amount).toLocaleString()}` },
    md.method && { key: 'method', node: md.method },
    md.receiptNumber && { key: 'receipt', node: <span className="font-mono text-[11px] text-muted">#{md.receiptNumber}</span> },
    md.period && { key: 'period', node: md.period },
    md.reason && { key: 'reason', node: `Reason: ${md.reason}` },
  ].filter(Boolean);

  return (
    <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_170px_70px] items-center gap-x-3 gap-y-2 sm:gap-y-0 px-5 py-3.5 border-b border-divider last:border-0 hover:bg-nav-hover transition-colors">
      {/* Main — avatar + text block */}
      <div className="col-span-2 sm:col-span-1 min-w-0 flex items-center gap-3">
        {system ? (
          <div className="w-[34px] h-[34px] bg-nav-hover text-muted rounded-full flex items-center justify-center flex-shrink-0" title="Automated process">
            <MdSmartToy className="text-base" />
          </div>
        ) : (
          <div className="w-[34px] h-[34px] bg-primary-light text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
            {(log.actorName || '?')[0].toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1">
          {/* Line 1: actor name + role/automated as small muted text */}
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-sm font-medium text-secondary truncate">
              {system ? 'TrainAxis System' : log.actorName}
            </span>
            {system ? (
              <span className="text-[11px] text-muted flex-shrink-0">Automated</span>
            ) : role && role !== 'club_admin' && (
              <span className="text-[11px] text-muted capitalize flex-shrink-0">{role}</span>
            )}
          </div>

          {/* Line 2: detail, single line, ellipsis on overflow */}
          {detailParts.length > 0 && (
            <p className="text-[13px] text-secondary truncate leading-snug mt-0.5">
              {detailParts.map((p, i) => (
                <span key={p.key}>
                  {i > 0 && <span className="text-subtle mx-1.5">·</span>}
                  {p.node}
                </span>
              ))}
            </p>
          )}
        </div>
      </div>

      {/* Action pill — own column so all pills line up; wraps within the column if long */}
      <span className={`justify-self-start sm:justify-self-end inline-block max-w-full text-[11px] font-semibold px-2 py-0.5 rounded-full ${style}`}>
        {label}
      </span>

      {/* Timestamp — fixed narrow column, right-aligned */}
      <p className="justify-self-end text-xs text-muted whitespace-nowrap" title={absoluteTime(log.timestamp)}>
        {relativeTime(log.timestamp)}
      </p>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
function ActivityLog() {
  document.title = 'Activity Log — TrainAxis';
  const currentTheme = useTheme()?.currentTheme;
  const voc = useClubVocab();

  const [search,   setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [page,     setPage]     = useState(1);

  const [appliedSearch,   setAppliedSearch]   = useState('');
  const [appliedCategory, setAppliedCategory] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo,   setAppliedDateTo]   = useState('');

  const params = {
    limit: 20,
    page: appliedDateFrom || appliedDateTo || appliedSearch || appliedCategory ? 1 : page,
  };
  if (appliedSearch)   params.search  = appliedSearch;
  if (appliedDateFrom) params.dateFrom = appliedDateFrom;
  if (appliedDateTo)   params.dateTo   = appliedDateTo;

  // Category → multiple action filter: handled client-side on the returned logs
  const { data, isPending } = useQuery({
    queryKey: ['activity-log', params],
    queryFn:  () => getActivityLog(params).then(r => r.data),
    staleTime: 30000,
  });

  const allLogs = data?.logs || [];
  const categoryFilter = CATEGORY_ACTIONS[appliedCategory];
  const logs = categoryFilter
    ? allLogs.filter(l => categoryFilter.includes(l.action))
    : allLogs;

  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const hasFilters = appliedSearch || appliedCategory || appliedDateFrom || appliedDateTo;

  const handleApply = () => {
    setAppliedSearch(search);
    setAppliedCategory(category);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  const handleClear = () => {
    setSearch(''); setCategory(''); setDateFrom(''); setDateTo('');
    setAppliedSearch(''); setAppliedCategory(''); setAppliedDateFrom(''); setAppliedDateTo('');
    setPage(1);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/dashboard"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-nav-hover text-muted transition-colors"
          >
            <MdArrowBack className="text-lg" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-secondary flex items-center gap-2">
              <MdHistory className="text-primary" />
              Activity Log
            </h1>
            <p className="text-muted text-sm mt-0.5">
              Critical actions performed in your club
            </p>
          </div>
        </div>
        {total > 0 && (
          <span className="text-xs text-muted bg-nav-hover px-3 py-1.5 rounded-full">
            {total.toLocaleString()} events
          </span>
        )}
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">
              Search
            </label>
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-base" />
              <input
                type="text"
                placeholder="Actor or target name…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleApply()}
                className="w-full h-10 pl-9 pr-3 text-sm bg-card border border-divider rounded-full text-secondary placeholder:text-muted focus:outline-none focus:border-secondary"
              />
            </div>
          </div>

          <div className="min-w-[150px]">
            <label className="block text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">
              Category
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className={`${FIELD_CLS} cursor-pointer`}
            >
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.replace(/\bStudents\b/g, voc.members)}</option>
              ))}
            </select>
          </div>

          <div className="min-w-[130px]">
            <label className="block text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className={FIELD_CLS}
            />
          </div>

          <div className="min-w-[130px]">
            <label className="block text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className={FIELD_CLS}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleApply}
              className={`flex items-center gap-1.5 h-10 px-4 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium rounded-full hover:opacity-90 transition-all duration-200 cursor-pointer`}
            >
              <MdFilterAlt className="text-base" />
              Filter
            </button>
            {hasFilters && (
              <button
                onClick={handleClear}
                className="flex items-center gap-1.5 h-10 px-3 bg-card text-secondary border border-divider text-sm font-medium rounded-full hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 cursor-pointer"
              >
                <MdClear className="text-base" />
                Clear
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card className="!p-0 overflow-hidden">
        {isPending ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-14 bg-nav-hover animate-pulse rounded-xl" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center py-14 text-center px-6">
            <MdHistory className="text-5xl text-subtle mb-3" />
            <p className="text-sm font-semibold text-muted mb-1">
              {hasFilters ? 'No events match your filters' : 'No activity logged yet'}
            </p>
            <p className="text-xs text-muted max-w-xs">
              {hasFilters
                ? 'Try adjusting your filters or clearing them to see all events.'
                : `Critical actions like recording payments, approving expenses, and archiving ${voc.members.toLowerCase()} will appear here.`}
            </p>
            {hasFilters && (
              <button
                onClick={handleClear}
                className="mt-4 text-xs font-semibold text-primary hover:underline cursor-pointer"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div>
            {logs.map(log => <ActivityRow key={log._id} log={log} />)}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted">
            Page {page} of {pages} · {total.toLocaleString()} total events
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-xs font-semibold border border-divider rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-nav-hover transition-colors cursor-pointer"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="px-3 py-1.5 text-xs font-semibold border border-divider rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-nav-hover transition-colors cursor-pointer"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLog;
